import React, { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Switch } from "../../components/ui/switch";
import { adminCreatePromo, adminUpdatePromo, apiErrorMessage } from "../../api";

const today = () => new Date().toISOString().slice(0, 10);
const plusYear = () => { const d = new Date(); d.setFullYear(d.getFullYear() + 1); return d.toISOString().slice(0, 10); };
const blank = () => ({
  code: "", partner_name: "", partner_contact: "", discount_type: "percent", discount_value: 10,
  commission_percent: 5, starts_at: today(), expires_at: plusYear(), max_uses: "", min_order: "", active: true,
});

const inputCls = "w-full h-11 px-3 border border-[var(--sand)] bg-white text-sm focus:outline-none focus:border-[var(--copper)]";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-[10px] tracking-[0.2em] uppercase text-[var(--espresso)]/60 mb-1.5 block">{label}</span>
    {children}
  </label>
);

export const PromoFormDialog = ({ open, promo, onClose, onSaved }) => {
  const [f, setF] = useState(blank());
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) setF(promo ? { ...blank(), ...promo, max_uses: promo.max_uses ?? "", min_order: promo.min_order ?? "" } : blank());
  }, [open, promo]);

  const upd = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const body = {
      code: f.code.trim(), partner_name: f.partner_name.trim(), partner_contact: f.partner_contact.trim(),
      discount_type: f.discount_type, discount_value: Number(f.discount_value), commission_percent: Number(f.commission_percent),
      starts_at: f.starts_at, expires_at: f.expires_at, active: f.active,
      max_uses: f.max_uses === "" ? null : Number(f.max_uses), min_order: f.min_order === "" ? null : Number(f.min_order),
    };
    try {
      await (promo ? adminUpdatePromo(promo.id, body) : adminCreatePromo(body));
      toast.success(promo ? "Promo code updated" : "Promo code created");
      onSaved();
    } catch (err) {
      toast.error(apiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl rounded-none bg-[var(--cream)] max-h-[90vh] overflow-y-auto" data-testid="promo-form-dialog">
        <DialogHeader><DialogTitle className="font-serif-display text-3xl font-normal">{promo ? `Edit ${promo.code}` : "New promo code"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid sm:grid-cols-2 gap-4 mt-2">
          <Field label="Code*"><input required value={f.code} onChange={(e) => setF((x) => ({ ...x, code: e.target.value.toUpperCase() }))} placeholder="RAHUL10" data-testid="promo-code-input" className={`${inputCls} uppercase tracking-wider`} /></Field>
          <Field label="Partner name*"><input required value={f.partner_name} onChange={upd("partner_name")} data-testid="promo-partner-input" className={inputCls} /></Field>
          <Field label="Partner phone / email"><input value={f.partner_contact} onChange={upd("partner_contact")} data-testid="promo-contact-input" className={inputCls} /></Field>
          <Field label="Discount type">
            <select value={f.discount_type} onChange={upd("discount_type")} data-testid="promo-type-select" className={inputCls}>
              <option value="percent">Percentage (%)</option>
              <option value="fixed">Fixed amount (₹)</option>
            </select>
          </Field>
          <Field label={f.discount_type === "percent" ? "Discount (%)*" : "Discount (₹)*"}><input required type="number" min="0.01" step="0.01" value={f.discount_value} onChange={upd("discount_value")} data-testid="promo-value-input" className={inputCls} /></Field>
          <Field label="Commission (%)*"><input required type="number" min="0" max="100" step="0.01" value={f.commission_percent} onChange={upd("commission_percent")} data-testid="promo-commission-input" className={inputCls} /></Field>
          <Field label="Start date*"><input required type="date" value={f.starts_at} onChange={upd("starts_at")} data-testid="promo-start-input" className={inputCls} /></Field>
          <Field label="Expiry date*"><input required type="date" value={f.expires_at} onChange={upd("expires_at")} data-testid="promo-expiry-input" className={inputCls} /></Field>
          <Field label="Max uses (optional)"><input type="number" min="1" step="1" value={f.max_uses} onChange={upd("max_uses")} placeholder="Unlimited" data-testid="promo-maxuses-input" className={inputCls} /></Field>
          <Field label="Minimum order ₹ (optional)"><input type="number" min="0" step="1" value={f.min_order} onChange={upd("min_order")} placeholder="None" data-testid="promo-minorder-input" className={inputCls} /></Field>
          <div className="sm:col-span-2 flex items-center gap-3">
            <Switch checked={f.active} onCheckedChange={(v) => setF((x) => ({ ...x, active: v }))} data-testid="promo-active-switch" />
            <span className="text-sm">{f.active ? "Active" : "Inactive"}</span>
          </div>
          <div className="sm:col-span-2 flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="h-11 px-6 border border-[var(--sand)] text-xs tracking-[0.2em] uppercase">Cancel</button>
            <button type="submit" disabled={saving} data-testid="promo-save-btn" className="h-11 px-6 vc-btn-copper text-xs tracking-[0.2em] uppercase flex items-center gap-2 disabled:opacity-60">
              {saving && <Loader2 className="w-4 h-4 animate-spin" />} Save
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
